import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    getEventLocations,
    createEventLocation,
    deleteEventLocation,
    updateEventLocation,
    toggleEventLocationStatus,
} from "../redux/eventLocation/eventLocationSlice";
import { toast } from "react-toastify";
import { formatDate, getImageUrlHelper, truncateText } from "../helper/helper";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { processFileUpload, FILE_TYPES } from "../utils/fileUploadHelper";
import Avatar from "./Avtar/Avatar";
import PaginationHelper from "../helper/PaginationHelper";

const EventLocationLayer = () => {
    const dispatch = useDispatch();
    const { locations = [], total = 0 } = useSelector((state) => state.eventLocation);

    // ================= ADD STATE =================
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [thumbnail, setThumbnail] = useState(null);

    const [venueName, setVenueName] = useState("");
    const [address1, setAddress1] = useState("");
    const [address2, setAddress2] = useState("");
    const [city, setCity] = useState("");
    const [stateName, setStateName] = useState("");
    const [country, setCountry] = useState("");
    const [pincode, setPincode] = useState("");
    const [latitude, setLatitude] = useState("");
    const [longitude, setLongitude] = useState("");
    const [venueImage, setVenueImage] = useState(null);
    const [eventDate, setEventDate] = useState("");

    // ================= EDIT STATE =================
    const [editId, setEditId] = useState("");
    const [editName, setEditName] = useState("");
    const [editDescription, setEditDescription] = useState("");
    const [editThumbnail, setEditThumbnail] = useState(null);
    const [editEventDate, setEditEventDate] = useState("");

    const [editVenueName, setEditVenueName] = useState("");
    const [editAddress1, setEditAddress1] = useState("");
    const [editAddress2, setEditAddress2] = useState("");
    const [editCity, setEditCity] = useState("");
    const [editStateName, setEditStateName] = useState("");
    const [editCountry, setEditCountry] = useState("");
    const [editPincode, setEditPincode] = useState("");
    const [editLatitude, setEditLatitude] = useState("");
    const [editLongitude, setEditLongitude] = useState("");
    const [editVenueImage, setEditVenueImage] = useState(null);

    const [search, setSearch] = useState("");
    const [entries, setEntries] = useState(50);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedItem, setSelectedItem] = useState(null);
    const [thumbnailPreview, setThumbnailPreview] = useState(null);
    const [venuePreview, setVenuePreview] = useState(null);

    const [editThumbnailPreview, setEditThumbnailPreview] = useState(null);
    const [editVenuePreview, setEditVenuePreview] = useState(null);
    const [confirmLocation, setConfirmLocation] = useState(null);
    const [toggling, setToggling] = useState(false);

    useEffect(() => {
        dispatch(
            getEventLocations({
                search,
                page: currentPage,
                limit: entries,
            })
        );
    }, [dispatch, search, currentPage, entries]);

    const totalEntries = total;
    const totalPages = Math.ceil(totalEntries / entries) || 1;


    const quillModules = {
        toolbar: [
            [{ header: [1, 2, 3, false] }],
            ["bold", "italic", "underline"],
            [{ list: "ordered" }, { list: "bullet" }],
            ["link"],
            ["clean"]
        ]
    };

    const resetAddForm = () => {
        setName("");
        setDescription("");
        setThumbnail(null);
        setThumbnailPreview(null);
        setVenueImage(null);
        setVenuePreview(null);
        setEventDate("");
    };

    const resetEditForm = () => {
        setEditId("");
        setEditName("");
        setEditDescription("");
        setEditThumbnail(null);
        setEditEventDate("");
    };


    const handleImageUpload = async (file, type, isEdit = false) => {
        if (!file) return;

        const processedFile = await processFileUpload(file, {
            allowedTypes: FILE_TYPES.IMAGE,
        });

        if (!processedFile) return;

        const preview = URL.createObjectURL(processedFile);

        if (isEdit) {
            if (type === "thumbnail") {
                setEditThumbnail(processedFile);
                setEditThumbnailPreview(preview);
            } else {
                setEditVenueImage(processedFile);
                setEditVenuePreview(preview);
            }
        } else {
            if (type === "thumbnail") {
                setThumbnail(processedFile);
                setThumbnailPreview(preview);
            } else {
                setVenueImage(processedFile);
                setVenuePreview(preview);
            }
        }
    };

    const openEditModal = (item) => {
        setSelectedItem(item);

        setEditId(item.id);
        setEditName(item.name || "");
        setEditDescription(item.description || "");
        setEditEventDate(item.date ? item.date.split("T")[0] : "");
        setEditThumbnail(null);
        setEditThumbnailPreview(
            item.thumbnail ? getImageUrlHelper(item.thumbnail) : null
        );

        setEditVenuePreview(
            item?.venue?.image ? getImageUrlHelper(item.venue.image) : null
        );

        const venue = item.venue || {};

        setEditVenueName(venue.name || "");
        setEditAddress1(venue.address1 || "");
        setEditAddress2(venue.address2 || "");
        setEditCity(venue.city || "");
        setEditStateName(venue.state || "");
        setEditCountry(venue.country || "");
        setEditPincode(venue.pincode || "");
        setEditLatitude(venue.latitude || "");
        setEditLongitude(venue.longitude || "");
    };

    const handleCreateLocation = () => {
        if (!name.trim()) return toast.error("Location name required");
        if (!description.trim()) return toast.error("Description required");
        if (!thumbnail) return toast.error("Thumbnail required");
        if (!eventDate) return toast.error("Event date required");

        if (!venueName.trim()) return toast.error("Venue name required");
        if (!address1.trim()) return toast.error("Address required");
        if (!city.trim()) return toast.error("City required");
        if (!stateName.trim()) return toast.error("State required");
        if (!country.trim()) return toast.error("Country required");

        if (!pincode)
            return toast.error("Pincode required");

        if (!/^\d{1,10}$/.test(pincode))
            return toast.error("Pincode must be numeric and max 10 digits");

        if (!latitude || isNaN(latitude))
            return toast.error("Valid latitude required");

        if (!longitude || isNaN(longitude))
            return toast.error("Valid longitude required");


        dispatch(createEventLocation({
            name,
            description,
            date: eventDate,
            thumbnail,
            venueName,
            address1,
            address2,
            city,
            state: stateName,
            country,
            pincode,
            latitude,
            longitude,
            venueImage,
        }))
            .unwrap()
            .then(() => {
                toast.success("Created successfully");
                dispatch(
                    getEventLocations({
                        page: currentPage,
                        limit: entries,
                    })
                );
                document.querySelector("#addEventLocationModal .btn-close")?.click();
                resetAddForm();
            })
            .catch((err) => toast.error(err?.message));
    };

    const handleUpdateLocation = () => {
        if (!editName.trim()) return toast.error("Location name required");
        if (!editDescription.trim()) return toast.error("Description required");
        if (!editEventDate) return toast.error("Event date required");

        if (!editVenueName.trim()) return toast.error("Venue name required");
        if (!editAddress1.trim()) return toast.error("Address required");
        if (!editCity.trim()) return toast.error("City required");

        if (!editPincode)
            return toast.error("Pincode required");

        if (!/^\d{1,10}$/.test(editPincode))
            return toast.error("Pincode must be numeric and max 10 digits");
        dispatch(updateEventLocation({
            id: editId,
            data: {
                name: editName,
                description: editDescription,
                date: editEventDate,
                thumbnail: editThumbnail,
                venueName: editVenueName,
                address1: editAddress1,
                address2: editAddress2,
                city: editCity,
                state: editStateName,
                country: editCountry,
                pincode: editPincode,
                latitude: editLatitude,
                longitude: editLongitude,
                venueImage: editVenueImage,
            },
        }))
            .unwrap()
            .then(() => {
                toast.success("Updated successfully");
                dispatch(
                    getEventLocations({
                        page: currentPage,
                        limit: entries,
                    })
                );
                document.querySelector("#editEventLocationModal .btn-close")?.click();
                resetEditForm();
            })
            .catch((err) => toast.error(err?.message));
    };

    const handleDeleteLocation = (id) => {
        dispatch(deleteEventLocation(id))
            .unwrap()
            .then(() => {
                toast.success("Deleted successfully");
            })
            .catch((err) => {
                toast.error(err?.message || "Failed to delete");
            });
    };

    return (
        <>
            <div className="card h-100 p-0 radius-12">
                <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
                    <div className="d-flex align-items-center flex-wrap gap-3">
                        <span className="text-md fw-medium text-secondary-light mb-0">
                            Show
                        </span>

                        <select
                            className="form-select form-select-sm w-auto ps-12 py-6 radius-12 h-40-px"
                            value={entries}
                            onChange={(e) => {
                                setEntries(Number(e.target.value));
                                setCurrentPage(1);
                            }}
                        >
                            <option value="50">50</option>
                            <option value="100">100</option>
                            <option value="150">150</option>
                            <option value="200">200</option>
                        </select>

                        <form className="navbar-search">
                            <input
                                type="text"
                                className="bg-base h-40-px w-auto"
                                placeholder="Search"
                                value={search}
                                onChange={(e) => {
                                    setSearch(e.target.value);
                                    setCurrentPage(1);
                                }}
                            />
                            <Icon icon="ion:search-outline" className="icon" />
                        </form>
                    </div>

                    <div className="d-flex align-items-center gap-2">
                        <button
                            className="btn btn-primary text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2"
                            data-bs-toggle="modal"
                            data-bs-target="#addEventLocationModal"
                        >
                            <Icon icon="ic:baseline-plus" className="icon text-xl line-height-1" />
                            Add Event Location
                        </button>
                    </div>
                </div>

                <div className="card-body p-12">
                    <div className="table-responsive">
                        <table className="table bordered-table sm-table">
                            <thead>
                                <tr>
                                    <th>S.L</th>
                                    <th>Thumbnail</th>
                                    <th>Name</th>
                                    <th>Description</th>
                                    <th>Event Date</th>
                                    <th>Status</th>
                                    <th className="text-center">Action</th>
                                </tr>
                            </thead>

                            <tbody>
                                {locations.length > 0 ? (
                                    locations.map((item, index) => (
                                        <tr key={item.id}>
                                            <td>{(currentPage - 1) * entries + index + 1}</td>

                                            <td>
                                                <Avatar
                                                    name={item.name}
                                                    image={item.thumbnail}
                                                    uniqueKey={item.id}
                                                    size={28}
                                                />

                                            </td>

                                            <td>{truncateText(item.name)}</td>

                                            <td>
                                                {truncateText(item.description)}
                                            </td>

                                            <td>{formatDate(item.date)}</td>

                                            <td>
                                                <div className="d-flex flex-column align-items-center gap-4">
                                                    <label className="switch-toggle">
                                                        <input
                                                            type="checkbox"
                                                            checked={item.status}
                                                            onChange={() => setConfirmLocation(item)}
                                                        />
                                                        <span className="slider" />
                                                    </label>
                                                </div>
                                            </td>

                                            <td className="text-center">
                                                <div className="d-flex align-items-center gap-10 justify-content-center">
                                                    <button
                                                        type="button"
                                                        className="bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                        data-bs-toggle="modal"
                                                        data-bs-target="#editEventLocationModal"
                                                        onClick={() => openEditModal(item)}
                                                    >
                                                        <Icon icon="lucide:edit" className="menu-icon" />
                                                    </button>

                                                    {/* <button
                                                        type="button"
                                                        className="remove-item-btn bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                        onClick={() => handleDeleteLocation(item.id)}
                                                    >
                                                        <Icon icon="fluent:delete-24-regular" className="menu-icon" />
                                                    </button> */}

                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="7" className="text-center">
                                            No data found
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {confirmLocation && (
                        <div className="modal fade show d-block">
                            <div className="modal-dialog modal-dialog-centered">
                                <div className="modal-content radius-12 shadow-lg">
                                    <div className="modal-header border-0">
                                        <h5 className="fw-semibold">
                                            {confirmLocation.status ? "Disable Event Location" : "Activate Event Location"}
                                        </h5>
                                        <button
                                            className="btn-close"
                                            onClick={() => setConfirmLocation(null)}
                                        />
                                    </div>

                                    <div className="modal-body text-center">
                                        <p className="mb-2">
                                            Are you sure you want to{" "}
                                            <strong>
                                                {confirmLocation.status ? "disable" : "activate"}
                                            </strong>{" "}
                                            this event location?
                                        </p>
                                    </div>

                                    <div className="modal-footer border-0 justify-content-center gap-2">
                                        <button
                                            className="btn btn-light"
                                            onClick={() => setConfirmLocation(null)}
                                        >
                                            Cancel
                                        </button>

                                        <button
                                            className={`btn ${confirmLocation.status ? "btn-danger" : "btn-success"}`}
                                            disabled={toggling}
                                            onClick={async () => {
                                                try {
                                                    setToggling(true);
                                                    await dispatch(toggleEventLocationStatus(confirmLocation.id)).unwrap();
                                                    dispatch(getEventLocations({
                                                        search,
                                                        page: currentPage,
                                                        limit: entries,
                                                    }));
                                                    toast.success(
                                                        confirmLocation.status
                                                            ? "Event location disabled successfully"
                                                            : "Event location activated successfully"
                                                    );
                                                    setConfirmLocation(null);
                                                } catch (err) {
                                                    toast.error(err?.message || "Failed to update status");
                                                } finally {
                                                    setToggling(false);
                                                }
                                            }}
                                        >
                                            {toggling
                                                ? "Processing..."
                                                : confirmLocation.status
                                                    ? "Disable"
                                                    : "Activate"}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* PAGINATION */}
                    <PaginationHelper
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                        totalItems={total}
                        pageSize={entries}
                    />
                </div>
            </div>

            {/* ADD EVENT LOCATION MODAL */}
            <div
                className="modal fade"
                id="addEventLocationModal"
                tabIndex={-1}
                aria-hidden="true"
            >
                <div className="modal-dialog modal-lg modal-dialog-centered">
                    <div className="modal-content radius-16 bg-base">
                        <div className="modal-header py-16 px-24 border border-top-0 border-start-0 border-end-0">
                            <h5 className="modal-title">Add Event Location</h5>
                            <button className="btn-close" data-bs-dismiss="modal" />
                        </div>
                        <div className="modal-body p-24">
                            <div className="row">

                                {/* NAME */}
                                <div className="col-6 mb-20">
                                    <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                        Location Name <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        className="form-control radius-8"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                    />
                                </div>
                                <div className="col-6 mb-20">
                                    <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                        Event Date <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="date"
                                        className="form-control radius-8"
                                        value={eventDate}
                                        onChange={(e) => setEventDate(e.target.value)}
                                    />
                                </div>

                                {/* DESCRIPTION */}
                                <div className="col-12 mb-20">
                                    <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                        Description <span className="text-danger">*</span>
                                    </label>
                                    <ReactQuill value={description} onChange={setDescription} modules={quillModules} />
                                </div>


                                {/* THUMBNAIL */}
                                <div className="col-12 mb-20">
                                    <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                        Thumbnail <span className="text-danger">*</span>
                                    </label>
                                    <label
                                        htmlFor="thumbnailUpload"
                                        className="border radius-8 border-dashed d-flex align-items-center justify-content-center"
                                        style={{ height: "150px", cursor: "pointer" }}
                                    >
                                        {thumbnailPreview ? (
                                            <img src={thumbnailPreview} className="w-100 h-100 object-fit-cover radius-8" />
                                        ) : (
                                            <div className="text-center">
                                                <Icon icon="solar:camera-outline" />
                                                <p><small>(Only image allowed 16:9)</small></p>
                                            </div>
                                        )}
                                    </label>

                                    <input
                                        id="thumbnailUpload"
                                        type="file"
                                        hidden
                                        accept=".jpg,.jpeg,.png,.webp"
                                        onChange={(e) => handleImageUpload(e.target.files[0], "thumbnail")}
                                    />
                                </div>
                                <h6>Add Venue Details:</h6>

                                {/* VENUE NAME */}
                                <div className="col-12 mb-20">
                                    <label className="form-label">
                                        Venue Name <span className="text-danger">*</span>
                                    </label>
                                    <input className="form-control radius-8"
                                        value={venueName}
                                        onChange={(e) => setVenueName(e.target.value)}
                                    />
                                </div>

                                {/* ADDRESS */}
                                <div className="col-6 mb-20">
                                    <label className="form-label">Address 1 <span className="text-danger">*</span></label>
                                    <input className="form-control radius-8"
                                        value={address1}
                                        onChange={(e) => setAddress1(e.target.value)}
                                    />
                                </div>

                                <div className="col-6 mb-20">
                                    <label className="form-label">Address 2</label>
                                    <input className="form-control radius-8"
                                        value={address2}
                                        onChange={(e) => setAddress2(e.target.value)}
                                    />
                                </div>

                                {/* CITY / STATE */}
                                <div className="col-4 mb-20">
                                    <label className="form-label">City <span className="text-danger">*</span></label>
                                    <input className="form-control radius-8"
                                        value={city}
                                        onChange={(e) => setCity(e.target.value)}
                                    />
                                </div>

                                <div className="col-4 mb-20">
                                    <label className="form-label">State <span className="text-danger">*</span></label>
                                    <input className="form-control radius-8"
                                        value={stateName}
                                        onChange={(e) => setStateName(e.target.value)}
                                    />
                                </div>

                                <div className="col-4 mb-20">
                                    <label className="form-label">Country <span className="text-danger">*</span></label>
                                    <input className="form-control radius-8"
                                        value={country}
                                        onChange={(e) => setCountry(e.target.value)}
                                    />
                                </div>

                                {/* PINCODE */}
                                <div className="col-4 mb-20">
                                    <label className="form-label">Pincode <span className="text-danger">*</span></label>
                                    <input className="form-control radius-8"
                                        value={pincode}
                                        onChange={(e) => {
                                            const val = e.target.value.replace(/\D/g, "");
                                            if (val.length <= 10) setPincode(val);
                                        }}
                                    />
                                </div>

                                {/* LAT / LNG */}
                                <div className="col-4 mb-20">
                                    <label className="form-label">Latitude <span className="text-danger">*</span></label>
                                    <input type="number" className="form-control radius-8"
                                        value={latitude}
                                        onChange={(e) => setLatitude(e.target.value)}
                                    />
                                </div>

                                <div className="col-4 mb-20">
                                    <label className="form-label">Longitude <span className="text-danger">*</span></label>
                                    <input type="number" className="form-control radius-8"
                                        value={longitude}
                                        onChange={(e) => setLongitude(e.target.value)}
                                    />
                                </div>

                                {/* VENUE IMAGE */}
                                <div className="col-12 mb-20">
                                    <label className="form-label">
                                        Venue Image
                                    </label>
                                    <label
                                        htmlFor="venueUpload"
                                        className="border radius-8 border-dashed d-flex align-items-center justify-content-center"
                                        style={{ height: "150px", cursor: "pointer" }}
                                    >
                                        {venuePreview ? (
                                            <img src={venuePreview} className="w-100 h-100 object-fit-cover radius-8" />
                                        ) : (
                                            <div className="text-center">
                                                <Icon icon="solar:camera-outline" />
                                                <p><small>(Only image allowed 16:9)</small></p>
                                            </div>
                                        )}
                                    </label>

                                    <input
                                        id="venueUpload"
                                        type="file"
                                        hidden
                                        accept=".jpg,.jpeg,.png,.webp"
                                        onChange={(e) => handleImageUpload(e.target.files[0], "venue")}
                                    />
                                </div>

                                {/* ACTION */}
                                <div className="d-flex justify-content-center gap-3 mt-24">
                                    <button className="btn btn-danger" data-bs-dismiss="modal">Cancel</button>
                                    <button className="btn btn-primary" onClick={handleCreateLocation}>Create</button>
                                </div>

                            </div>
                        </div>

                    </div>
                </div>
            </div>

            {/* EDIT EVENT LOCATION MODAL */}
            <div
                className="modal fade"
                id="editEventLocationModal"
                tabIndex={-1}
                aria-hidden="true"
            >
                <div className="modal-dialog modal-lg modal-dialog-centered">
                    <div className="modal-content radius-16 bg-base">
                        <div className="modal-header py-16 px-24 border border-top-0 border-start-0 border-end-0">
                            <h5 className="modal-title">Edit Event Location</h5>
                            <button className="btn-close" data-bs-dismiss="modal" />
                        </div>

                        <div className="modal-body p-24">
                            <div className="row">

                                <div className="col-6 mb-20">
                                    <label className="form-label">Location Name</label>
                                    <input className="form-control radius-8"
                                        value={editName}
                                        onChange={(e) => setEditName(e.target.value)}
                                    />
                                </div>
                                <div className="col-6 mb-20">
                                    <label className="form-label">
                                        Event Date <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="date"
                                        className="form-control radius-8"
                                        value={editEventDate}
                                        onChange={(e) => setEditEventDate(e.target.value)}
                                    />
                                </div>

                                <div className="col-12 mb-20">
                                    <label className="form-label">Description</label>
                                    <ReactQuill value={editDescription} onChange={setEditDescription} modules={quillModules} />
                                </div>

                                {/* THUMBNAIL */}
                                <div className="col-12 mb-20">
                                    <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                        Thumbnail <span className="text-danger">*</span>
                                    </label>
                                    <label htmlFor="editThumbnailUpload" className="border radius-8 border-dashed d-flex align-items-center justify-content-center"
                                        style={{ height: "150px", cursor: "pointer" }}>

                                        {editThumbnailPreview ? (
                                            <img src={editThumbnailPreview} className="w-100 h-100 object-fit-cover radius-8" />
                                        ) : (
                                            <div className="text-center">
                                                <Icon icon="solar:camera-outline" />
                                                <p><small>(Only image allowed)</small></p>
                                            </div>
                                        )}
                                    </label>

                                    <input
                                        id="editThumbnailUpload"
                                        type="file"
                                        hidden
                                        accept=".jpg,.jpeg,.png,.webp"
                                        onChange={(e) => handleImageUpload(e.target.files[0], "thumbnail", true)}

                                    />
                                </div>
                                <h6>Edit Venue Details:</h6>

                                <div className="col-12 mb-20">
                                    <label className="form-label">Venue Name</label>
                                    <input className="form-control radius-8"
                                        value={editVenueName}
                                        onChange={(e) => setEditVenueName(e.target.value)}
                                    />
                                </div>

                                <div className="col-6 mb-20">
                                    <label className="form-label">Address 1 <span className="text-danger">*</span></label>
                                    <input className="form-control radius-8"
                                        value={editAddress1}
                                        onChange={(e) => setEditAddress1(e.target.value)}
                                    />
                                </div>

                                <div className="col-6 mb-20">
                                    <label className="form-label">Address 2</label>
                                    <input className="form-control radius-8"
                                        value={editAddress2}
                                        onChange={(e) => setEditAddress2(e.target.value)}
                                    />
                                </div>

                                <div className="col-4 mb-20">
                                    <label className="form-label">City <span className="text-danger">*</span></label>
                                    <input className="form-control radius-8"
                                        value={editCity}
                                        onChange={(e) => setEditCity(e.target.value)}
                                    />
                                </div>

                                <div className="col-4 mb-20">
                                    <label className="form-label">State <span className="text-danger">*</span></label>
                                    <input className="form-control radius-8"
                                        value={editStateName}
                                        onChange={(e) => setEditStateName(e.target.value)}
                                    />
                                </div>

                                <div className="col-4 mb-20">
                                    <label className="form-label">Country <span className="text-danger">*</span></label>
                                    <input className="form-control radius-8"
                                        value={editCountry}
                                        onChange={(e) => setEditCountry(e.target.value)}
                                    />
                                </div>

                                <div className="col-4 mb-20">
                                    <label className="form-label">Pincode <span className="text-danger">*</span></label>
                                    <input className="form-control radius-8"
                                        value={editPincode}
                                        onChange={(e) => {
                                            const val = e.target.value.replace(/\D/g, "");
                                            if (val.length <= 10) setEditPincode(val);
                                        }}
                                    />
                                </div>

                                <div className="col-4 mb-20">
                                    <label className="form-label">Latitude <span className="text-danger">*</span></label>
                                    <input type="number" className="form-control radius-8"
                                        value={editLatitude}
                                        onChange={(e) => setEditLatitude(e.target.value)}
                                    />
                                </div>

                                <div className="col-4 mb-20">
                                    <label className="form-label">Longitude <span className="text-danger">*</span></label>
                                    <input type="number" className="form-control radius-8"
                                        value={editLongitude}
                                        onChange={(e) => setEditLongitude(e.target.value)}
                                    />
                                </div>

                                <div className="col-12 mb-20">
                                    <label className="form-label">
                                        Venue Image
                                    </label>
                                    <label htmlFor="editVenueUpload" className="border radius-8 border-dashed d-flex align-items-center justify-content-center"
                                        style={{ height: "150px", cursor: "pointer" }}>

                                        {editVenuePreview ? (
                                            <img src={editVenuePreview} className="w-100 h-100 object-fit-cover radius-8" />
                                        ) : (
                                            <div className="text-center">
                                                <Icon icon="solar:camera-outline" />
                                                <p><small>(Only image allowed)</small></p>
                                            </div>
                                        )}
                                    </label>

                                    <input
                                        type="file"
                                        id="editVenueUpload"

                                        hidden
                                        accept=".jpg,.jpeg,.png,.webp"
                                        onChange={(e) => handleImageUpload(e.target.files[0], "venue", true)}
                                    />
                                </div>

                                <div className="d-flex justify-content-center gap-3 mt-24">
                                    <button className="btn btn-danger" data-bs-dismiss="modal">Cancel</button>
                                    <button className="btn btn-primary" onClick={handleUpdateLocation}>Update</button>
                                </div>

                            </div>
                        </div>
                    </div>
                </div>
            </div>

        </>
    );
};

export default EventLocationLayer;
